import { useState, useRef, useEffect, useCallback } from 'react';
import { Heart } from 'lucide-react';
import { HUG, MY_NAME } from '@/config/valentineContent';

const STORAGE_KEY = 'valentine_hug_count_v1';
const MILESTONES = HUG.milestones;

const warmthLevel = (count) => {
  if (count >= 10) return { label: 'Infinite', pct: 100 };
  if (count >= 9) return { label: 'Legend', pct: 90 };
  if (count >= 8) return { label: 'Glowing', pct: 80 };
  if (count >= 7) return { label: 'Spoiling', pct: 70 };
  if (count >= 6) return { label: 'Snuggly', pct: 60 };
  if (count >= 5) return { label: 'Bear Hug', pct: 50 };
  if (count >= 4) return { label: 'Cuddle', pct: 40 };
  if (count >= 3) return { label: 'Warm', pct: 30 };
  if (count >= 2) return { label: 'Soft', pct: 20 };
  if (count >= 1) return { label: 'First Spark', pct: 10 };
  return { label: 'Sending...', pct: 0 };
};

const readCount = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  const n = parseInt(raw ?? '0', 10);
  return Number.isFinite(n) ? n : 0;
};

const writeCount = (n) => localStorage.setItem(STORAGE_KEY, String(n));

export default function VirtualHug() {
  const [hugCount, setHugCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [milestone, setMilestone] = useState(null);
  const [floatingHearts, setFloatingHearts] = useState([]);
  const [ripples, setRipples] = useState([]);
  const [floatingPlus, setFloatingPlus] = useState([]);
  const [embraceActive, setEmbraceActive] = useState(false);
  const containerRef = useRef(null);
  const heartIdRef = useRef(0);

  useEffect(() => {
    setHugCount(readCount());

    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setHugCount(readCount());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const spawnFloatingHearts = useCallback(() => {
    const newHearts = Array.from({ length: 8 }, () => ({
      id: ++heartIdRef.current,
      x: (Math.random() - 0.5) * 160,
      y: -(Math.random() * 80 + 60),
      size: Math.random() * 10 + 10,
      delay: Math.random() * 300,
      rotation: (Math.random() - 0.5) * 60,
    }));
    setFloatingHearts((prev) => [...prev, ...newHearts]);
    setTimeout(() => {
      setFloatingHearts((prev) => prev.filter((h) => !newHearts.includes(h)));
    }, 1800);
  }, []);

  const spawnRipples = useCallback(() => {
    const ts = Date.now();
    const newRipples = [
      { id: ts, delay: 0 },
      { id: ts + 1, delay: 150 },
      { id: ts + 2, delay: 300 },
    ];
    setRipples((prev) => [...prev, ...newRipples]);
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => !newRipples.includes(r)));
    }, 1500);
  }, []);

  const spawnPlusOne = useCallback(() => {
    const id = Date.now();
    const p = { id, x: (Math.random() - 0.5) * 40, y: 0 };
    setFloatingPlus((prev) => [...prev, p]);
    setTimeout(() => {
      setFloatingPlus((prev) => prev.filter((item) => item.id !== id));
    }, 1200);
  }, []);

  const resetHugs = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setHugCount(0);
    setMilestone(null);
  }, []);

  const sendHug = async () => {
    if (sending) return;
    setSending(true);
    setEmbraceActive(true);

    spawnFloatingHearts();
    spawnRipples();
    spawnPlusOne();

    const newCount = hugCount + 1;
    setHugCount(newCount);
    writeCount(newCount);

    const hit = MILESTONES.find((m) => m.at === newCount);
    if (hit) {
      setMilestone(hit.label);
      setTimeout(() => setMilestone(null), 2500);
    }

    setTimeout(() => setEmbraceActive(false), 1000);
    setTimeout(() => setSending(false), 1200);
  };

  const warmth = warmthLevel(hugCount);
  const deliveredTo = MY_NAME;

  return (
    <section id="virtual-hug" data-testid="virtual-hug-section" className="section-romantic text-center">
      <div className="max-w-md mx-auto">
        <h2 className="font-heading text-2xl md:text-3xl mb-2" style={{ color: 'var(--text-primary)' }}>
          {HUG.heading}
        </h2>
        <p className="font-body text-xs mb-10" style={{ color: 'var(--text-secondary)' }}>
          {HUG.subtitle}
        </p>

        <div className="relative flex items-center justify-center mb-8" style={{ height: 220 }} ref={containerRef}>
          <div className="hug-ambient-ring hug-ring-1" style={{ borderColor: 'var(--accent-glow)' }} />
          <div className="hug-ambient-ring hug-ring-2" style={{ borderColor: 'var(--accent-glow)' }} />
          <div className="hug-ambient-ring hug-ring-3" style={{ borderColor: 'var(--accent-glow)' }} />

          {ripples.map((r) => (
            <div
              key={r.id}
              className="hug-click-ripple"
              style={{ animationDelay: `${r.delay}ms`, borderColor: 'var(--accent-light)' }}
            />
          ))}

          <div className={`hug-embrace-left ${embraceActive ? 'hug-embrace-active' : ''}`}>
            <svg width="60" height="100" viewBox="0 0 60 100" fill="none">
              <path d="M60 90 C60 90, 45 85, 30 60 C15 35, 5 25, 5 10" stroke="var(--accent-light)" strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.6" />
            </svg>
          </div>
          <div className={`hug-embrace-right ${embraceActive ? 'hug-embrace-active' : ''}`}>
            <svg width="60" height="100" viewBox="0 0 60 100" fill="none">
              <path d="M0 90 C0 90, 15 85, 30 60 C45 35, 55 25, 55 10" stroke="var(--accent-light)" strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.6" />
            </svg>
          </div>

          {floatingHearts.map((h) => (
            <span
              key={h.id}
              className="hug-burst-heart"
              style={{
                '--hx': `${h.x}px`,
                '--hy': `${h.y}px`,
                '--hr': `${h.rotation}deg`,
                fontSize: h.size,
                animationDelay: `${h.delay}ms`,
                color: 'var(--accent-primary)',
              }}
            >
              &#9829;
            </span>
          ))}

          {floatingPlus.map((p) => (
            <span
              key={p.id}
              className="hug-plus-one font-body"
              style={{ '--px': `${p.x}px`, color: 'var(--accent-primary)' }}
            >
              +1
            </span>
          ))}

          <button
            data-testid="send-hug-btn"
            onClick={sendHug}
            className={`hug-main-btn relative z-10 w-28 h-28 rounded-full flex items-center justify-center transition-all duration-300 ${sending ? 'scale-110' : 'hover:scale-105 active:scale-95'
              }`}
            style={{
              background: `radial-gradient(circle at 40% 35%, var(--accent-light), var(--accent-primary) 70%)`,
              boxShadow: sending
                ? '0 0 50px var(--accent-glow), 0 0 100px var(--accent-glow)'
                : '0 8px 30px var(--accent-glow)',
            }}
          >
            <Heart
              size={40}
              fill="#fff"
              strokeWidth={0}
              className={`transition-transform duration-300 ${sending ? 'hug-heart-pulse' : 'hug-heart-idle'}`}
            />
          </button>

          {milestone && (
            <div className="hug-milestone" style={{ color: 'var(--accent-primary)' }}>
              <span className="font-heading text-sm">{milestone}</span>
            </div>
          )}
        </div>

        <div className="h-8 flex items-center justify-center mb-6">
          {sending ? (
            <p className="hug-delivered-text font-body text-sm" style={{ color: 'var(--text-primary)' }}>
              Hug delivered to {deliveredTo} 💗
            </p>
          ) : (
            <p className="font-body text-xs" style={{ color: 'var(--text-secondary)', opacity: 0.6 }}>
              Tap the heart
            </p>
          )}
        </div>

        <div className="max-w-xs mx-auto mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-body text-[10px] tracking-wider uppercase" style={{ color: 'var(--text-secondary)' }}>
              Warmth Level
            </span>
            <span className="font-body text-[10px] font-bold tracking-wider uppercase" style={{ color: 'var(--accent-primary)' }}>
              {warmth.label}
            </span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--accent-glow)' }}>
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${warmth.pct}%`,
                background: `linear-gradient(90deg, var(--accent-light), var(--accent-primary))`,
              }}
            />
          </div>
        </div>

        <div className="flex items-center justify-center gap-2">
          <Heart size={12} fill="var(--accent-primary)" strokeWidth={0} />
          <p className="font-body text-sm" style={{ color: 'var(--text-secondary)' }}>
            <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{hugCount}</span> hugs sent
          </p>

          {/* tiny reset word */}
          <button
            type="button"
            onClick={resetHugs}
            className="ml-2 font-body text-[10px] underline opacity-50 hover:opacity-90 transition"
            style={{ color: 'var(--text-secondary)' }}
            aria-label="Reset hug counter"
            title="reset"
          >
            reset
          </button>
        </div>
      </div>
    </section>
  );
}
